import React, { useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';

import Modal from '../../../Components/UI/Modal/Modal';
import { finalizeJobArtisan } from '../../../Store/Actions/actionOffer';
import { useI18n } from '../../../i18n/I18nProvider';

const JobFinalisingModal = ({
  showModal,
  handleClick,
  demand,
  firebase,
  loading,
  finalizeDemand,
}) => {
  const { t, formatCurrency } = useI18n();
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const opened = showModal === 'open';
  const contract = demand?.contract;
  const profile = firebase?.profile || {};
  const userId = firebase?.auth?.uid;

  const close = () => {
    if (!submitting) {
      setAdditionalInfo('');
      setConfirmed(false);
      setError('');
      handleClick?.();
    }
  };

  const submit = async () => {
    if (submitting) return;
    if (!userId || !demand?.demand_id || !contract) {
      setError(t('finalising.sessionMissing'));
      return;
    }
    if (!confirmed) {
      setError(t('finalising.confirmRequired'));
      return;
    }

    setError('');
    setSubmitting(true);
    const success = await finalizeDemand({
      demandId: demand.demand_id,
      artisanId: userId,
      artisanFirstName: profile.firstName,
      artisanLastName: profile.lastName,
      artisanEmail: profile.email,
      artisanProfileImage: profile.profile_image,
      artisanAddress: profile.address,
      descriptionOfContract: contract.description,
      startDate: contract.start_date,
      contactNumber: contract.artisan_phone_number,
      endDate: new Date(),
      postDate: contract.post_date,
      materialCost: contract.material_cost,
      materialList: Array.isArray(contract.material_list) ? contract.material_list : [],
      wage: contract.wage,
      totalCost: contract.total_cost,
      additionalInfo: additionalInfo.trim(),
    });
    setSubmitting(false);
    if (success) close();
  };

  return (
    <Modal opened={opened} close={close}>
      <Header>
        <div>
          <Eyebrow>{t('finalising.eyebrow')}</Eyebrow>
          <Title>{t('finalising.title')}</Title>
          <Subtitle>{t('finalising.subtitle')}</Subtitle>
        </div>
        <CloseButton type="button" aria-label={t('common.close')} onClick={close}>×</CloseButton>
      </Header>

      {!contract ? (
        <EmptyState>{t('finalising.missing')}</EmptyState>
      ) : (
        <>
          <Summary>
            <div><span>{t('finalising.client')}</span><strong>{demand.user_firstname || t('common.client')}</strong></div>
            <div><span>{t('finalising.amount')}</span><strong>{formatCurrency(contract.total_cost)}</strong></div>
          </Summary>

          <Field>
            <label htmlFor="final-information">{t('finalising.extra')}</label>
            <textarea
              id="final-information"
              value={additionalInfo}
              onChange={(event) => setAdditionalInfo(event.target.value)}
              placeholder={t('finalising.placeholder')}
              maxLength={1200}
             
            />
            <Counter>{additionalInfo.length}/1200</Counter>
          </Field>

          <Confirmation>
            <input
              id="finish-confirmation"
              type="checkbox"
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
            <label htmlFor="finish-confirmation">
              {t('finalising.confirmation')}
            </label>
          </Confirmation>

          <Warning>
            {t('finalising.warning')}
          </Warning>
          {error && <ErrorMessage role="alert">{error}</ErrorMessage>}

          <Actions>
            <SecondaryButton type="button" onClick={close} disabled={submitting || loading}>{t('finalising.notYet')}</SecondaryButton>
            <PrimaryButton type="button" onClick={submit} disabled={submitting || loading}>
              {submitting ? t('finalising.confirming') : t('finalising.confirm')}
            </PrimaryButton>
          </Actions>
        </>
      )}
    </Modal>
  );
};

const Header = styled.header`display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; margin-bottom: 1.2rem;`;
const Eyebrow = styled.div`margin-bottom: 0.35rem; color: #a88a5d; font-size: 0.76rem; font-weight: 900; letter-spacing: 0.12em; text-transform: uppercase;`;
const Title = styled.h2`margin: 0; color: #111111; font-size: clamp(1.3rem, 4vw, 1.85rem);`;
const Subtitle = styled.p`margin: 0.4rem 0 0; color: #726b61; line-height: 1.5;`;
const CloseButton = styled.button`width: 2.5rem; height: 2.5rem; flex: 0 0 auto; border: 0; border-radius: 50%; background: #f1ece5; color: #111111; font: inherit; font-size: 1.6rem; cursor: pointer;`;
const Summary = styled.div`
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.8rem; margin-bottom: 1rem;
  div { display: grid; gap: 0.25rem; padding: 1rem; border-radius: 0.9rem; background: #faf8f4; }
  span { color: #726b61; font-size: 0.74rem; text-transform: uppercase; }
  @media (max-width: 520px) { grid-template-columns: 1fr; }
`;
const Field = styled.div`
  display: grid; gap: 0.4rem;
  label { color: #222222; font-weight: 800; }
  textarea { width: 100%; min-height: 8rem; padding: 0.8rem; border: 1px solid #ddd3c4; border-radius: 0.8rem; resize: vertical; color: #111111; }
`;
const Counter = styled.span`justify-self: end; color: #7a8790; font-size: 0.76rem;`;
const Confirmation = styled.div`
  display: grid; grid-template-columns: auto 1fr; align-items: start; gap: 0.65rem; margin-top: 1rem; padding: 1rem; border: 1px solid #dce4e9; border-radius: 0.9rem; background: #fbfcfd;
  input { width: 1.1rem; height: 1.1rem; margin-top: 0.15rem; accent-color: #1f7446; }
  label { color: #284154; line-height: 1.5; cursor: pointer; }
`;
const Warning = styled.p`margin: 1rem 0 0; padding: 0.85rem 0.95rem; border-radius: 0.8rem; background: #f4ede1; color: #8a6f48; line-height: 1.5;`;
const ErrorMessage = styled.p`margin: 0.8rem 0 0; color: #a52e27; font-size: 0.88rem;`;
const EmptyState = styled.p`margin: 0; padding: 1rem; border-radius: 0.8rem; background: #faf8f4; color: #66615b;`;
const Actions = styled.div`display: flex; justify-content: flex-end; gap: 0.7rem; margin-top: 1.25rem; @media (max-width: 520px) { flex-direction: column-reverse; }`;
const BaseButton = styled.button`min-height: 2.8rem; padding: 0.72rem 1.05rem; border-radius: 999px; font: inherit; font-weight: 800; cursor: pointer; &:disabled { opacity: 0.55; cursor: wait; }`;
const PrimaryButton = styled(BaseButton)`border: 1px solid #24754a; background: #2e8a59; color: #fff;`;
const SecondaryButton = styled(BaseButton)`border: 1px solid #e7dfd2; background: #fff; color: #111111;`;

const mapStateToProps = ({ firebase, demands }) => ({ firebase, loading: demands?.loading });
const mapDispatchToProps = (dispatch) => ({ finalizeDemand: (payload) => dispatch(finalizeJobArtisan(payload)) });

export default connect(mapStateToProps, mapDispatchToProps)(JobFinalisingModal);
